import React, { Component } from "react";
import PropTypes from "prop-types";
import pick from "lodash/pick";
import debounce from "lodash/debounce";
import { getProductDetail } from "@shared/apis/cprod";
import { pricePreview as searchPreview } from "@shared/apis/order";
import { moduleEdit } from "@shared/lingxi/core";
import { getHistoryForReplacing } from "@shared/core/history";
import { urlSearchParams, urlQueryParams } from "@shared/core/url-search-params";
import Button from "@components/button";
import BookDates from "@views/book/book-dates";
import GuestCount from "./guest-count";
import OrderPreview from "./order-preview";
import { getProductQRCodeUrl } from "@views/product-detail/helpers";

class Booking extends Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      queryData: props.defaultQueryData,
      displayVerifyInfo: false,
      product: props.product,
      qrcodeUrl: "",
    };
    this.handleBookDatesChange = this.handleBookDatesChange.bind(this);
    this.updateOrderPreviewDebounced = debounce(this.updateOrderPreview.bind(this), 300);
  }

  componentDidMount() {
    window.addEventListener("scroll", this.handleDocumentScroll);
    window.$bookingParams = { ...this.state.queryData };
    this.history = getHistoryForReplacing();
    this._historyUnlisten = this.history.listen(location => {
      const queryData = urlQueryParams(location.search);
      this.setState({ queryData }, this.updateOrderPreviewDebounced);
      // 给房东咨询用的特殊逻辑，正常情况下应该用事件沟通的，但是咨询按钮的js在html里面，不好用事件
      window.$bookingParams = { ...queryData };
    });

    this.setState({
      qrcodeUrl: getProductQRCodeUrl({
        productId: this.state.product.productId,
        utmParam: "utm_source=www&&utm_campaign=xcxh&utm_content=bk_button",
      }),
    });

    this.handleDocumentScroll();
    this.updateOrderPreview(!!this.state.product);
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleDocumentScroll);
    if (this._historyUnlisten) {
      this._historyUnlisten();
    }
  }

  handleDocumentScroll() {
    const main = document.getElementById("bookingMain");
    const dom = document.getElementById("bookingPanel");
    const top = (document.documentElement && document.documentElement.scrollTop) || document.body.scrollTop;
    if (document.body.clientHeight < main.clientHeight || document.body.clientWidth < 992) {
      // 宽度小于992或窗口高度不够就不fixed了
      if (main.className.indexOf("fixed") !== -1) {
        main.className = main.className.replace(" fixed", "");
      }
    } else if (top > dom.offsetTop) {
      if (main.className.indexOf("fixed") === -1) {
        main.className += " fixed";
      }
    } else if (main.className.indexOf("fixed") !== -1) {
      main.className = main.className.replace(" fixed", "");
    }
  }

  _singleRequest(name, promise) {
    this._requestManager = this._requestManager || {};
    try {
      this._requestManager[name].abort(); // @TODO 可是 promise 没有 resolve 也没有 reject，视作没有释放
    } catch (e) {
      // do nothing
    } finally {
      this._requestManager[name] = null;
    }
    this._requestManager[name] = promise.Request;
    return promise;
  }

  getOrderPreview(withoutProduct = false) {
    const queryData = this.state.queryData;
    const productId = this.props.product.productId;
    return Promise.all([
      this.props.isLogin
        ? this._singleRequest(
            "search preview",
            searchPreview({
              checkinGuests: 1,
              ...pick(queryData, ["checkinDate", "checkoutDate", "checkinGuests"]),
              productId,
              autoChooseDiscount: true,
            })
          ).then(res => res.body.data)
        : Promise.resolve(null),
      withoutProduct
        ? null
        : this._singleRequest(
            "product detail",
            getProductDetail({
              productId,
              startDate: queryData.checkinDate,
              endDate: queryData.checkoutDate,
            })
          ).then(res => res.body.data),
    ]);
  }

  updateOrderPreview(withoutProduct = false) {
    const queryData = this.state.queryData;
    if (!(queryData.checkinDate && queryData.checkoutDate)) return;
    this.getOrderPreview(withoutProduct).then(([orderPreview, product]) => {
      this.setState({
        orderPreview,
        product: product || this.state.product,
      });
    });
  }

  handleBookDatesChange({ checkin, checkout }, type) {
    const { queryData } = this.state;
    const oldCheckin = queryData.checkinDate;
    const oldCheckout = queryData.checkoutDate;

    const priceUpdateNeeded = oldCheckin !== checkin || oldCheckout !== checkout;

    const { productId } = this.props.product;
    moduleEdit("b_6y35hvkw", {
      goods_id: productId,
      custom: {
        module_name: type === "ci" ? "入住日期" : "退房日期",
        checkin_date: checkin,
        checkout_date: checkout,
      },
    });

    if (!priceUpdateNeeded) return;

    const history = this.history;
    const { location } = history;
    history.replace(
      `${location.pathname}${urlSearchParams({
        ...queryData,
        checkinDate: checkin,
        checkoutDate: checkout,
      })}`
    );
  }

  handleCheckinGuestsChange(checkinGuests) {
    const oldQuery = this.state.queryData;
    if (oldQuery.checkinGuests === checkinGuests) return;
    const history = this.history;
    const { location } = history;
    history.replace(
      `${location.pathname}${urlSearchParams({
        ...oldQuery,
        checkinGuests,
      })}`
    );
  }

  render() {
    const { queryData, orderPreview, product } = this.state;
    const { productRpInfo, productId, price, discountPrice, discountShow } = product;
    const maxNights = (productRpInfo && productRpInfo.maxBookingDays) || Infinity;
    const minNights = (productRpInfo && productRpInfo.minBookingDays) || 1;

    const hasDiscount = !!(discountPrice || discountPrice === 0);

    const bookingType = product.productRpInfo.bookingType === 0 ? "立即预订" : "申请预订";
    const bkPwu = this.props.pwu || { type: "pc_bk_button", source: "weixin" };
    return (
      <aside id="bookingMain">
        <header>
          {!discountShow || <span className="product-discount">{discountShow}</span>}

          <span className="product-price" itemProp="offers" itemScope={true} itemType="http://schema.org/Offer">
            <span className="currency" itemProp="priceCurrency" content="CNY">
              ￥
            </span>
            <span itemProp="price">{(hasDiscount ? discountPrice : price) / 100}</span>
            <small>/晚</small>
          </span>

          {!hasDiscount || (
            <del className="product-price product-price--original">
              <span className="currency">￥</span>
              <span>{price / 100}</span>
              <small>/晚</small>
            </del>
          )}
        </header>
        <div className="booking-wrapper">
          <BookDates
            productId={productId}
            onChange={this.handleBookDatesChange}
            value={{
              checkin: queryData.checkinDate,
              checkout: queryData.checkoutDate,
            }}
            minNights={minNights}
            maxNights={maxNights}
            originalPrice={product.price}
          />
          <GuestCount
            max={product.maxGuestNumber}
            value={+queryData.checkinGuests || 1}
            onChange={checkinGuests => this.handleCheckinGuestsChange(checkinGuests)}
          />
          <p className="booking-info">
            {productRpInfo.maxAdditionalGuests
              ? `可住${productRpInfo.maxCheckinGuests}人，可加客${
                  productRpInfo.maxAdditionalGuests
                }人，另外收取${productRpInfo.additionalChargePerGuest / 100}元/人`
              : `可住${productRpInfo.maxCheckinGuests}人，不可加客`}
          </p>
          {orderPreview ? <OrderPreview orderPreview={orderPreview} /> : null}

          <form action={`/booking/house/${product.productId}`} method="GET" className="action-group">
            {!queryData.checkinDate || <input type="hidden" name="checkin" value={queryData.checkinDate} />}
            {!queryData.checkoutDate || <input type="hidden" name="checkout" value={queryData.checkoutDate} />}
            {!queryData.checkinGuests || <input type="hidden" name="guests" value={queryData.checkinGuests} />}
            {!queryData.effectId || <input type="hidden" name="effectId" value={queryData.effectId} />}

            {product.verifyStatus === 5 ? (
              <Button
                className={`btn--booking btn--booking-${product.productRpInfo.bookingType}`}
                type="submit"
                btnType="primary"
                btnSize="lg"
              >
                <span className="text-click">{bookingType}</span>
              </Button>
            ) : (
              <Button type="button" btnType="primary" btnSize="lg" disabled={true}>
                暂不可订
              </Button>
            )}
            {!this.state.qrcodeUrl || (
              <div className="qr-container">
                <img src={this.state.qrcodeUrl} alt="房源二维码" />
                <div className="qr-text">
                  <p>微信扫码9折订</p>
                </div>
              </div>
            )}
          </form>
        </div>
      </aside>
    );
  }
}

Booking.propTypes = {
  isLogin: PropTypes.bool,
  product: PropTypes.object.isRequired,
  defaultQueryData: PropTypes.object,
  pwu: PropTypes.object,
};

Booking.defaultProps = {
  isLogin: false,
  defaultQueryData: {},
  pwu: null,
};

export default Booking;

require("./index.less");
require("./index.less");